import React, { useState } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, HeartPulse, Plus, X, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { METABOLIC_CONDITIONS, ALLERGIES_OPTIONS } from './settings/SettingsHealthProfile';

const HealthProfileOnboardingModal = ({ open, onComplete }) => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();

  const [conditions, setConditions] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [newInjury, setNewInjury] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleTag = (list, setList, id) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        health_profile: {
          metabolic_conditions: conditions,
          allergies_intolerances: allergies,
          injury_history: injuries,
        }
      };
      await api.put('/users/profile', payload);
      updateUser({ ...user, health_profile: payload.health_profile });
      toast.success(language === 'fr' ? 'Profil santé activé !' : 'Health profile activated!');
      onComplete();
    } catch {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    // Save empty profile so the modal doesn't show again
    try {
      const payload = {
        health_profile: {
          metabolic_conditions: [],
          allergies_intolerances: [],
          injury_history: [],
        }
      };
      await api.put('/users/profile', payload);
      updateUser({ ...user, health_profile: payload.health_profile });
    } catch { /* silent */ }
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto" data-testid="health-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-red-400" />
            {language === 'fr' ? 'Mise à jour de ton Profil Tactique' : 'Update your Tactical Profile'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {language === 'fr'
              ? 'Le Coach YUMIX peut maintenant adapter ses conseils à tes blessures et à ton métabolisme. 30 secondes pour le rendre plus intelligent.'
              : 'Coach YUMIX can now adapt advice to your injuries and metabolism. 30 seconds to make it smarter.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Metabolic Conditions */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground uppercase tracking-wide">
              {language === 'fr' ? 'Conditions Métaboliques' : 'Metabolic Conditions'}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {METABOLIC_CONDITIONS.map(opt => {
                const isActive = conditions.includes(opt.id);
                return (
                  <button key={opt.id} type="button"
                    onClick={() => toggleTag(conditions, setConditions, opt.id)}
                    data-testid={`hp-modal-condition-${opt.id}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                      isActive ? 'bg-primary/20 border-primary text-primary' : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/40'
                    }`}>
                    <span className="mr-1">{opt.icon}</span>
                    {language === 'fr' ? opt.fr : opt.en}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Allergies */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground uppercase tracking-wide">
              {language === 'fr' ? 'Allergies / Intolérances' : 'Allergies / Intolerances'}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ALLERGIES_OPTIONS.map(opt => {
                const isActive = allergies.includes(opt.id);
                return (
                  <button key={opt.id} type="button"
                    onClick={() => toggleTag(allergies, setAllergies, opt.id)}
                    data-testid={`hp-modal-allergy-${opt.id}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                      isActive ? 'bg-orange-500/20 border-orange-500 text-orange-400' : 'bg-secondary/50 border-border text-muted-foreground hover:border-orange-500/40'
                    }`}>
                    <span className="mr-1">{opt.icon}</span>
                    {language === 'fr' ? opt.fr : opt.en}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Injuries */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground uppercase tracking-wide">
              {language === 'fr' ? 'Blessures / Zones Fragiles' : 'Injuries / Weak Spots'}
            </label>
            <div className="flex gap-2">
              <Input
                value={newInjury}
                onChange={(e) => setNewInjury(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const t = newInjury.trim();
                    if (t && !injuries.includes(t)) {
                      setInjuries(prev => [...prev, t]);
                      setNewInjury('');
                    }
                  }
                }}
                placeholder={language === 'fr' ? 'Ex: Genou droit fragile' : 'Ex: Weak right knee'}
                className="bg-secondary border-border text-sm flex-1"
                data-testid="hp-modal-injury-input"
              />
              <Button type="button" size="sm"
                onClick={() => {
                  const t = newInjury.trim();
                  if (t && !injuries.includes(t)) {
                    setInjuries(prev => [...prev, t]);
                    setNewInjury('');
                  }
                }}
                disabled={!newInjury.trim()}
                className="px-3"
                data-testid="hp-modal-injury-add-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {injuries.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {injuries.map((inj, idx) => (
                  <div key={idx} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-500/15 border border-purple-500/30 text-purple-300">
                    <span>{inj}</span>
                    <button type="button" onClick={() => setInjuries(prev => prev.filter((_, i) => i !== idx))} className="hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-primary text-primary-foreground font-heading uppercase"
            data-testid="hp-modal-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {language === 'fr' ? 'Activer la protection' : 'Activate protection'}
          </Button>
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs text-muted-foreground hover:text-foreground text-center py-1 transition-colors"
            data-testid="hp-modal-skip-btn"
          >
            {language === 'fr' ? 'Aucune condition — passer cette étape' : 'No conditions — skip this step'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HealthProfileOnboardingModal;
